import { isoWeek, nextSprint, sprintEnd, sprintKeyOf, sprintStart } from '@nedelnik/shared';

import type { Period, SprintKey, SprintType } from '@nedelnik/shared';

/**
 * Названия периодов.
 *
 * Спринт не хранится, а считается из даты (раздел 3.5 архитектуры), поэтому
 * подпись — тоже чистая функция от ключа. Месяцы берутся у `Intl`, а не из
 * своего списка: в подписи «24–30 августа» нужен родительный падеж, а в
 * заголовке «Август 2026» — именительный, и оба варианта `Intl` для русского
 * знает сам.
 */

const MONTH_ALONE = new Intl.DateTimeFormat('ru-RU', { month: 'long' });
/** «сент.», «авг.» — для кнопок, где ширина дороже полноты. */
const MONTH_SHORT = new Intl.DateTimeFormat('ru-RU', { month: 'short' });
const DAY_MONTH = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long' });

/** Месяц в том падеже, в каком он стоит рядом с числом: «августа», а не «август». */
function monthWithDay(date: Date): string {
  const part = DAY_MONTH.formatToParts(date).find((item) => item.type === 'month');
  return part?.value ?? '';
}

function capitalize(value: string): string {
  return value.length === 0 ? value : value[0]?.toUpperCase() + value.slice(1);
}

/**
 * Год виден, только когда он не нынешний.
 *
 * «Сентябрь 2026» в сентябре 2026-го — это лишние пять знаков в самом узком
 * месте экрана: на телефоне из-за них стрелка «вперёд» уезжала на вторую
 * строку, и шапка ломалась пополам. Убрать год совсем нельзя — шагать по
 * месяцам можно сколько угодно далеко, и сентябрь позапрошлого года ничем не
 * отличался бы от нынешнего.
 *
 * У недель то же правило и та же причина: номер недели повторяется каждый год.
 */
function yearSuffix(start: Date, now: Date, separator = ' '): string {
  return start.getFullYear() === now.getFullYear()
    ? ''
    : `${separator}${String(start.getFullYear())}`;
}

/** Заголовок периода: «Неделя 37», «Сентябрь», «Август 2025», «Входящие». */
export function periodTitle(period: Period, now = new Date()): string {
  if (period === 'inbox') return 'Входящие';

  const start = sprintStart(period);
  // У недели между номером и годом точка: два числа подряд («Неделя 35 2025»)
  // читаются как одно длинное.
  if (period.startsWith('w:')) {
    return `Неделя ${String(isoWeek(start))}${yearSuffix(start, now, ' · ')}`;
  }
  return `${capitalize(MONTH_ALONE.format(start))}${yearSuffix(start, now)}`;
}

/** Подзаголовок: календарный отрезок, который этот спринт занимает. */
export function periodSubtitle(period: Period): string {
  if (period === 'inbox') return 'задачи вне спринтов';

  const start = sprintStart(period);
  const end = sprintEnd(period);

  if (start.getMonth() === end.getMonth()) {
    return `${String(start.getDate())}–${String(end.getDate())} ${monthWithDay(end)}`;
  }
  return `${DAY_MONTH.format(start)} — ${DAY_MONTH.format(end)}`;
}

/**
 * Короткая подпись для кнопок: «н37», «сент.», «авг. 2025».
 *
 * Месяц именно сокращённый. В шапке на телефоне четыре кнопки в ряд, и полное
 * «сентябрь» на кнопке возврата не оставляло места остальным — ряд ломался на
 * две строки. У недели такой беды нет: «н37» и так короче некуда.
 */
export function periodShort(sprint: SprintKey | null, now = new Date()): string {
  if (sprint === null) return 'входящие';

  const start = sprintStart(sprint);
  if (sprint.startsWith('w:')) return `н${String(isoWeek(start))}${yearSuffix(start, now, ' · ')}`;
  return `${MONTH_SHORT.format(start)}${yearSuffix(start, now)}`;
}

/**
 * Нужны ли рядом с именем даты.
 *
 * У недели — да: «н37» сам по себе не говорит ничего, планируют по числам. У
 * месяца — нет: «сентябрь · 1–30 сентября» повторяет одно и то же дважды.
 */
export function periodNeedsDates(sprint: SprintKey): boolean {
  return sprint.startsWith('w:');
}

/**
 * Месяцы в предложном падеже.
 *
 * Остальные падежи `Intl` даёт сам, и в проекте есть правило не заводить свой
 * список месяцев. Предложного он не даёт вовсе, а без него полоса фильтра
 * говорила «в сентябрь» — фраза, которую в готовом продукте видеть стыдно.
 */
const MONTHS_IN = [
  'январе',
  'феврале',
  'марте',
  'апреле',
  'мае',
  'июне',
  'июле',
  'августе',
  'сентябре',
  'октябре',
  'ноябре',
  'декабре',
];

/**
 * Где ищем, целой фразой: «в Неделе 37», «в сентябре», «во Входящих».
 *
 * Неделя названа словом, как в шапке, а не «н37»: сокращение экономило место
 * в полосе, но читалось как опечатка (решение владельца, 2026-09-08).
 */
export function periodIn(period: Period, now = new Date()): string {
  if (period === 'inbox') return 'во Входящих';

  const start = sprintStart(period);
  if (period.startsWith('w:')) {
    return `в Неделе ${String(isoWeek(start))}${yearSuffix(start, now, ' · ')}`;
  }
  return `в ${MONTHS_IN[start.getMonth()] ?? ''}${yearSuffix(start, now)}`;
}

/** Тот ли это спринт, в котором мы сейчас живём (3.2.4). */
export function isCurrentSprint(period: Period, type: SprintType, now: Date): boolean {
  return period !== 'inbox' && period === sprintKeyOf(now, type);
}

/**
 * Подпись текущего периода, согласованная с его родом: «Неделя 35 — текущая»,
 * «Август 2026 — текущий». Одна форма на оба случая всегда с чем-нибудь да не
 * согласуется, а несогласованная подпись читается как недоделка.
 */
export function currentLabel(period: Period): string {
  return period.startsWith('m:') ? 'текущий' : 'текущая';
}

export const STATUS_LABELS = {
  backlog: 'Бэклог',
  wip: 'В работе',
  done: 'Готово',
} as const;

/**
 * Имя спринта относительно текущего: «Текущая неделя», «Следующая неделя».
 *
 * Для подсказки свайпа и плашки переноса (3.1.12): там решают на ходу, и
 * «следующая» читается быстрее, чем «Неделя 43 · 19–25 октября» (решение
 * владельца 2026-10-18). Остальные спринты — обычным заголовком: «следующая»
 * от прошлой недели — это не следующая от сегодня.
 */
export function relativeSprintTitle(
  sprint: SprintKey,
  current: SprintKey,
  now = new Date(),
): string {
  const week = sprint.startsWith('w:');
  if (sprint === current) return week ? 'Текущая неделя' : 'Текущий месяц';
  if (sprint === nextSprint(current)) return week ? 'Следующая неделя' : 'Следующий месяц';
  return periodTitle(sprint, now);
}

/**
 * Куда уехала задача — для плашки отмены (3.1.12): «→ В следующую неделю»,
 * «→ Во Входящие», «→ В неделю 45». Без «Перенесено»: плашка с «Отменить»
 * сама говорит, что что-то случилось, а с этим словом фраза не помещалась в
 * одну строку на телефоне. Стрелка осталась — она и читается как «перенесено»
 * (замечания владельца 2026-10-18).
 */
export function moveTargetPhrase(
  sprint: SprintKey | null,
  current: SprintKey,
  now = new Date(),
): string {
  return `→ ${movePlace(sprint, current, now)}`;
}

function movePlace(sprint: SprintKey | null, current: SprintKey, now = new Date()): string {
  if (sprint === null) return 'Во Входящие';
  const week = sprint.startsWith('w:');
  if (sprint === current) return week ? 'В текущую неделю' : 'В текущий месяц';
  if (sprint === nextSprint(current)) return week ? 'В следующую неделю' : 'В следующий месяц';
  const title = periodTitle(sprint, now);
  return week ? `В неделю ${title.slice('Неделя '.length)}` : `В ${title.toLowerCase()}`;
}
